import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Button, MenuItem, Select, FormControl, InputLabel,
  Grid, CircularProgress, TextField, useTheme, Paper, alpha, Chip, Fade
} from '@mui/material';
import AnalyticsWidgets from './AnalyticsWidgets';
import FloatingUsageAnalytics from './FloatingUsageAnalytics';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { format, parseISO } from 'date-fns';
import axios from 'axios';
import Cookies from '../../../common/Cookies';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import FilterListRoundedIcon from '@mui/icons-material/FilterListRounded';
import ChatRoundedIcon from '@mui/icons-material/ChatRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';

const DATE_PRESETS = [
  { label: 'Month to Date', value: 'monthToDate' },
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Last 7 Days', value: 'last7' },
  { label: 'Last Month', value: 'lastMonth' },
  { label: 'Custom Range', value: 'custom' },
];

function getPresetRange(preset) {
  const now = new Date();
  let start, end;
  switch (preset) {
    case 'today':
      start = end = now;
      break;
    case 'yesterday':
      start = end = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
      break;
    case 'last7':
      end = now;
      start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 6);
      break;
    case 'lastMonth':
      end = new Date(now.getFullYear(), now.getMonth(), 0);
      start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      break;
    case 'monthToDate':
    default:
      start = new Date(now.getFullYear(), now.getMonth(), 1);
      end = now;
      break;
  }
  return [start, end];
}

const formatDate = (d) => {
  if (!d) return '';
  try { return format(typeof d === 'string' ? parseISO(d) : new Date(d), 'MMM d'); } catch { return d; }
};

const formatDateFull = (d) => {
  if (!d) return '';
  try { return format(typeof d === 'string' ? parseISO(d) : new Date(d), 'MMM d, yyyy'); } catch { return d; }
};

function CustomTooltip({ active, payload, label, valueLabel, color }) {
  const theme = useTheme();
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{
      bgcolor: 'background.paper',
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 2,
      px: 2, py: 1,
      boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`,
    }}>
      <Typography variant="caption" color="text.secondary">{formatDateFull(label)}</Typography>
      <Typography variant="body2" sx={{ fontWeight: 700, color }}>
        {payload[0]?.value?.toLocaleString()} {valueLabel}
      </Typography>
    </Box>
  );
}

function ChartSection({ title, icon, data, dataKey, color, gradientId, valueLabel, total, emptyMsg }) {
  const theme = useTheme();
  return (
    <Paper elevation={0} sx={{
      p: 3,
      borderRadius: 3,
      border: `1px solid ${theme.palette.divider}`,
      transition: 'box-shadow 0.2s ease',
      '&:hover': { boxShadow: `0 4px 20px ${alpha(color, 0.1)}` },
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(color, 0.1), color,
          }}>
            {icon}
          </Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
        {total != null && (
          <Chip
            label={`Total: ${total.toLocaleString()}`}
            size="small"
            sx={{
              fontWeight: 700,
              bgcolor: alpha(color, 0.08),
              color,
              fontSize: '0.8rem',
            }}
          />
        )}
      </Box>
      {(!data || data.length === 0) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 220 }}>
          <Typography color="text.secondary">{emptyMsg}</Typography>
        </Box>
      ) : (
        <ResponsiveContainer width="100%" height={280}>
          <AreaChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={color} stopOpacity={0.2} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} vertical={false} />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip content={<CustomTooltip valueLabel={valueLabel} color={color} />} />
            <Area
              type="monotone"
              dataKey={dataKey}
              stroke={color}
              strokeWidth={2.5}
              fill={`url(#${gradientId})`}
              dot={false}
              activeDot={{ r: 5, strokeWidth: 2, fill: '#fff', stroke: color }}
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </Paper>
  );
}

export default function Analytics() {
  const theme = useTheme();
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState('all');
  const [datePreset, setDatePreset] = useState('monthToDate');
  const [[startDate, endDate], setDateRange] = useState(getPresetRange('monthToDate'));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ chats: [], messages: [] });
  const [widgets, setWidgets] = useState(null);
  const [customRange, setCustomRange] = useState([null, null]);

  useEffect(() => {
    axios.post('/agents/getAllAgents').then(res => {
      setAgents([{ id: 'all', agent_name: 'All Agents' }, ...res.data]);
      setSelectedAgent('all');
    });
  }, []);

  const handlePresetChange = (e) => {
    setDatePreset(e.target.value);
    if (e.target.value !== 'custom') {
      setDateRange(getPresetRange(e.target.value));
      setCustomRange([null, null]);
    }
  };

  const handleApply = async () => {
    setLoading(true);
    setError(null);
    try {
      const tenantId = Cookies.getCookies('tenantId');
      const exclusiveEndDate = new Date(endDate);
      exclusiveEndDate.setDate(exclusiveEndDate.getDate() + 1);
      const req = {
        tenantId,
        agentId: selectedAgent === 'all' ? '' : selectedAgent,
        startDate: startDate.toISOString().slice(0, 10),
        endDate: exclusiveEndDate.toISOString().slice(0, 10),
      };
      const [overviewRes, widgetsRes] = await Promise.all([
        axios.post('/analytics/overview', req),
        axios.post('/analytics/widgets', req)
      ]);
      setData(overviewRes.data);
      setWidgets(widgetsRes.data);
    } catch (e) {
      setError('Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedAgent !== undefined && selectedAgent !== null) {
      handleApply();
    }
    // eslint-disable-next-line
  }, [selectedAgent]);

  const totalChats = data.chats?.length > 0 ? data.chats.reduce((sum, d) => sum + (d.count || 0), 0) : 0;
  const totalMessages = data.messages?.length > 0 ? data.messages.reduce((sum, d) => sum + (d.count || 0), 0) : 0;

  return (
    <Fade in timeout={500}>
      <Box sx={{ width: '100%', mx: 'auto', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 }, position: 'relative' }}>

        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2.5,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
            color: '#fff',
          }}>
            <InsightsRoundedIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800 }}>
              Analytics
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Detailed insights into your chatbot performance
            </Typography>
          </Box>
        </Box>

        {/* Filter Bar */}
        <Paper elevation={0} sx={{
          mb: 3,
          p: 2.5,
          borderRadius: 3,
          border: `1px solid ${theme.palette.divider}`,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterListRoundedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
              Filters
            </Typography>
          </Box>
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: datePreset === 'custom' ? '1.2fr 1.2fr 1.2fr 1.2fr 0.6fr' : '1.5fr 1.5fr 0.6fr'
            },
            gap: 2,
            alignItems: 'center',
          }}>
            <FormControl fullWidth size="small">
              <InputLabel>Agent</InputLabel>
              <Select
                value={selectedAgent}
                label="Agent"
                onChange={e => setSelectedAgent(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                {agents.map(agent => (
                  <MenuItem key={agent.id} value={agent.id}>{agent.agent_name || agent.agentName}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select
                value={datePreset}
                label="Date Range"
                onChange={handlePresetChange}
                sx={{ borderRadius: 2 }}
              >
                {DATE_PRESETS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {datePreset === 'custom' && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Start Date"
                  value={customRange[0]}
                  onChange={date => {
                    setCustomRange([date, customRange[1]]);
                    setDateRange([date, customRange[1]]);
                  }}
                  slotProps={{ textField: { size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
                />
              </LocalizationProvider>
            )}
            {datePreset === 'custom' && (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="End Date"
                  value={customRange[1]}
                  onChange={date => {
                    setCustomRange([customRange[0], date]);
                    setDateRange([customRange[0], date]);
                  }}
                  slotProps={{ textField: { size: 'small', sx: { '& .MuiOutlinedInput-root': { borderRadius: 2 } } } }}
                />
              </LocalizationProvider>
            )}
            <Button
              variant="contained"
              onClick={handleApply}
              disabled={loading}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 700,
                height: 40,
                boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`,
              }}
            >
              Apply
            </Button>
          </Box>
        </Paper>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 6 }}><CircularProgress /></Box>
        ) : error ? (
          <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
            <Typography color="error">{error}</Typography>
          </Paper>
        ) : (
          <>
            {/* Charts */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6} sx={{ width: '45%' }}>
                <ChartSection
                  title="Number of Chats"
                  icon={<ChatRoundedIcon />}
                  data={data.chats}
                  dataKey="count"
                  color={theme.palette.primary.main}
                  gradientId="analyticsChatsGradient"
                  valueLabel="chats"
                  total={totalChats > 0 ? totalChats : null}
                  emptyMsg="No chat data available for this period."
                />
              </Grid>
              <Grid item xs={12} md={6} sx={{ width: '45%' }}>
                <ChartSection
                  title="Number of Messages"
                  icon={<MailOutlineRoundedIcon />}
                  data={data.messages}
                  dataKey="count"
                  color={theme.palette.secondary.main || '#9c27b0'}
                  gradientId="analyticsMessagesGradient"
                  valueLabel="messages"
                  total={totalMessages > 0 ? totalMessages : null}
                  emptyMsg="No message data available for this period."
                />
              </Grid>
            </Grid>

            {/* Widgets */}
            <AnalyticsWidgets widgets={widgets} />
          </>
        )}

        <FloatingUsageAnalytics />
      </Box>
    </Fade>
  );
}
